import React from 'react'
import dayjs from 'dayjs'
import { TFunction, useTranslation } from 'react-i18next'
import {
  ActionListItem,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  Box as Table,
  Box as TableBody,
  Text,
  useSidePanel,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ContactCase } from 'types/contacts'
import { PaymentContextType } from 'types/payments'
import { ContactCaseListIcon } from './components/ContactCaseListIcon'
import { ContactCaseSummary } from './components/ContactCaseSummary'

interface ContactCaseListProps {
  contactCases: ContactCase[]
  handleAssignContactClick: (contactCase: ContactCase) => void
}

const getHighlightDescription = (
  paymentContextType: PaymentContextType,
  t: TFunction<'translation', undefined>
) => {
  switch (paymentContextType) {
    case PaymentContextType.CHANNEL_PAYMENT:
      return t('contacts.channelPayment')
    case PaymentContextType.PAYMENT_IN:
      return t('contacts.cryptoPayment')
    case PaymentContextType.DEPOSIT:
      return t('contacts.directDeposit')
    default:
      return
  }
}

export const ContactCaseList = ({
  contactCases,
  handleAssignContactClick,
}: ContactCaseListProps) => {
  const { t } = useTranslation()

  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.payments
  const canEdit = permissions?.canEdit

  const {
    isSidePanelOpen: isContactCaseDetailsOpen,
    sidePanelData: contactCase,
    paging: pageContactCases,
    setIsSidePanelOpen: setIsContactCaseDetailsOpen,
    updateSidePanel: updateContactCaseDetails,
  } = useSidePanel<ContactCase>(contactCases)

  const onDetailsClick = (contactCaseIndex: number) => {
    updateContactCaseDetails(contactCaseIndex)
    setIsContactCaseDetailsOpen(true)
  }

  return (
    <Box divideChildren>
      <Table tag="table" width="full" data-testid="contact-case-list">
        <TableBody tag="tbody" width="full">
          {contactCases?.map((contactCase, contactCaseIndex) => {
            const {
              hash,
              amount,
              currencyCode,
              network,
              paymentCreated,
              paymentContext: { type: paymentContextType } = {},
            } = contactCase || {}

            const title = `${amount} ${currencyCode}(${network})`

            const headerHighlightLeft = (
              <Highlight
                iconElement={<ContactCaseListIcon paymentContextType={paymentContextType} />}
                type="feature"
                variant="emphasis"
              >
                {getHighlightDescription(paymentContextType, t)}
              </Highlight>
            )

            return (
              <ActionListItem
                id={hash}
                key={hash}
                title={title}
                HeaderHighlightLeft={headerHighlightLeft}
                Avatar={
                  <Avatar type="currency" name={currencyCode ? currencyCode?.toLowerCase() : ''} />
                }
                BodyPre={
                  <Text variant="body-text" color="subdued" className="leading-tight">
                    {dayjs(paymentCreated).format(DateFormat.DD_MMM_YYYY_HH_MM_A)}
                  </Text>
                }
                CustomAction={
                  <DropdownMenu
                    DropdownMenuItems={[
                      <DropdownMenuItem
                        key={`action-info-${hash}`}
                        LeadingIcon={<Icon name="Info" />}
                        onClick={() => onDetailsClick(contactCaseIndex)}
                      >
                        {t('details')}
                      </DropdownMenuItem>,
                    ]}
                  >
                    <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
                  </DropdownMenu>
                }
                ActionButton={
                  <Button
                    disabled={!canEdit || viewOnly}
                    iconElement={<Icon name="UserCirclePlus" />}
                    variant="ghost"
                    onClick={() => handleAssignContactClick(contactCase)}
                  >
                    {t('contacts.assignContact')}
                  </Button>
                }
                border
                onItemClick={() => onDetailsClick(contactCaseIndex)}
              />
            )
          })}
        </TableBody>
      </Table>

      <ContactCaseSummary
        isOpen={isContactCaseDetailsOpen}
        contactCase={contactCase}
        pageBeneficiaries={pageContactCases}
        onClose={() => setIsContactCaseDetailsOpen(false)}
      />
    </Box>
  )
}
